import type { QueryKey } from '@tanstack/react-query';

import {
  apiQuery,
  createQueryKeys,
  useApiMutation,
  type ApiMutationOptions,
  type ApiMutationResult,
} from '@/shared/data';

import * as api from './automatic-split.api';
import type { CancelBody, InstallmentStatusBody, SaveBody } from './automatic-split.wire';

// Cache e mutações do Split automático (shared/data). Sem texto de UI nem toast (X06): o feedback é da
// tela, com os textos do legado. Um hook genérico de mutação por módulo (X07).

export const automaticSplitKeys = createQueryKeys('automatic-split');

/** `GET …/Operation/{id}` cru; a lista e o plano leem pelas fontes (`automatic-split.sources.ts`). */
export const automaticSplitDetailQuery = (id: string) =>
  apiQuery(automaticSplitKeys.detail(id), ({ signal }) => api.getAutomaticSplit(id, signal), { enabled: id !== '' });

interface MutationDef<V, D> {
  readonly run: (variables: V) => Promise<D>;
  readonly invalidate: (variables: V) => readonly QueryKey[];
}

const onModule = <V, D>(run: (variables: V) => Promise<D>): MutationDef<V, D> => ({ run, invalidate: () => [automaticSplitKeys.all] });

/** Uma entrada por chamada de escrita do fio. */
export const automaticSplitMutations = {
  simulate: { run: (body: Record<string, unknown>) => api.simulateAutomaticSplit(body), invalidate: () => [] } satisfies MutationDef<Record<string, unknown>, unknown>,
  save: onModule(({ id, body }: { id: string | undefined; body: SaveBody }) => (id === undefined ? api.createAutomaticSplit(body) : api.updateAutomaticSplit(id, body))),
  escrowBeneficiaries: { run: (params: Record<string, unknown>) => api.listEscrowBeneficiaries(params), invalidate: () => [] } satisfies MutationDef<Record<string, unknown>, unknown>,
  installmentStatus: onModule(({ installmentId, body }: { installmentId: string; body: InstallmentStatusBody }) => api.updateInstallmentStatus(installmentId, body)),
  cancel: onModule(({ id, body }: { id: string; body: CancelBody }) => api.cancelAutomaticSplit(id, body)),
} as const;

export type AutomaticSplitMutationName = keyof typeof automaticSplitMutations;
type Def<K extends AutomaticSplitMutationName> = (typeof automaticSplitMutations)[K];
export type AutomaticSplitMutationVariables<K extends AutomaticSplitMutationName> = Parameters<Def<K>['run']>[0];
export type AutomaticSplitMutationData<K extends AutomaticSplitMutationName> = Awaited<ReturnType<Def<K>['run']>>;

/** Mutação de uma chamada de escrita; sem toast (`success: false, error: false`, X06): a tela avisa como o legado. */
export function useAutomaticSplitMutation<K extends AutomaticSplitMutationName>(
  name: K,
  options: Omit<ApiMutationOptions<AutomaticSplitMutationData<K>, AutomaticSplitMutationVariables<K>>, 'mutationFn'> = {},
): ApiMutationResult<AutomaticSplitMutationData<K>, AutomaticSplitMutationVariables<K>> {
  const def = automaticSplitMutations[name] as unknown as MutationDef<AutomaticSplitMutationVariables<K>, AutomaticSplitMutationData<K>>;
  return useApiMutation<AutomaticSplitMutationData<K>, AutomaticSplitMutationVariables<K>>({
    mutationKey: ['automatic-split', 'mutation', name],
    invalidate: (_data, variables) => def.invalidate(variables),
    success: false,
    error: false,
    ...options,
    mutationFn: def.run,
  });
}
